import React, { useReducer, useState } from 'react';
import {
  Modal, Box, TextField, Button, Typography, MenuItem, Snackbar, Alert, CircularProgress
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import axios from 'axios';

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 500,
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  p: 4,
  maxHeight: "80vh",
  overflowY: "auto"
};

// Reducer function
function userCreationReducer(state, action) {
  switch (action.type) {
    case 'set_firstName':
      return { ...state, firstName: action.payload };
    case 'set_middleName':
      return { ...state, middleName: action.payload };
    case 'set_lastName':
      return { ...state, lastName: action.payload };
    case 'set_email':
      return { ...state, email: action.payload };
    case 'set_gender':
      return { ...state, gender: action.payload };
    case 'set_dob':
      return { ...state, dob: action.payload };
    case 'set_phone':
      return { ...state, phone: action.payload };
    case 'reset_form':
      return {
        firstName: '',
        middleName: '',
        lastName: '',
        email: '',
        gender: '',
        dob: null,
        phone: ''
      }
    default:
      return state;
  }
}

export default function UserCreationModal({ open, onClose }) {

  const  [state, dispatch] = useReducer(
    userCreationReducer,
    {
      firstName: '',
      middleName: '',
      lastName: '',
      email: '',
      gender: '',
      dob: null,
      phone: ''
    }
  )

  const [isSubmitting, setIsSubmitting] = useState(false);
  // Snackbar states
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Snackbar handlers
  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const registerPatient = async (userData) => {
    try {
      const response = await axios.post(
        'https://cauth.unitedwecare.com/users/register/patient',
        userData,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem("Authorization")?.split(" ")[1] || ''}`,
            'Content-Type': 'application/json',
            'x-api-key': process.env.REACT_APP_ADMIN_API_KEY
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('Error registering patient:', error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    // Check if at least one contact method is provided (email or phone)
    if (!state.email && !state.phone) {
      showSnackbar('Please provide either email or phone number.', 'warning');
      return;
    }

    // Validate date of birth
    if (!state.dob) {
      showSnackbar('Please select a date of birth.', 'warning');
      return;
    }

    // Email validation (only if email is provided)
    if (state.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(state.email)) {
        showSnackbar('Please enter a valid email address.', 'warning');
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const userData = {
        firstName: state.firstName,
        middleName: state.middleName,
        lastName: state.lastName,
        name: `${state.firstName} ${state.middleName} ${state.lastName}`,
        email: state.email,
        dob: dayjs(state.dob).format('YYYY-MM-DD'),
        gender: state.gender || 'male',
        phone: state.phone
      };

      const result = await registerPatient(userData);
      
      if (result.status === 200 && result.message === "User already exists") {
        showSnackbar("A patient with this email or phone number already exists.", "error");
      } else if (result.status === 200 || result.status === 201) {
        showSnackbar(result.message, "success");
        // Close modal after a short delay
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        showSnackbar(result.message, "error");
      }
      
      // Reset form
      dispatch({type: 'reset_form'});
      
      // Close modal after a short delay
      // setTimeout(() => {
      //   onClose();
      // }, 1500);
      
    } catch (error) {
      console.error('Registration error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to create user. Please try again.';
      showSnackbar(errorMessage, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Modal open={open} onClose={onClose} aria-labelledby="create-user-modal">
        <Box sx={modalStyle}>
          <Typography variant="h6" mb={2}>Create New Patient</Typography>

          <TextField
            fullWidth
            label="First Name"
            value={state.firstName}
            onChange={e => {
              dispatch({type: 'set_firstName', payload: e.target.value});
            }}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            label="Middle Name"
            value={state.middleName}
            onChange={e => {
              dispatch({type: 'set_middleName', payload: e.target.value});
            }}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            label="Last Name"
            value={state.lastName}
            onChange={e => {
              dispatch({type: 'set_lastName', payload: e.target.value});
            }}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />

          <TextField
            fullWidth
            type="email"
            label="Email"
            value={state.email}
            onChange={e => {
              dispatch({type: 'set_email', payload: e.target.value});
            }}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />
          <TextField
            fullWidth
            type="phone"
            label="Phone"
            value={state.phone}
            onChange={e => {
              let phoneValue = e.target.value;
              
              // Remove any existing +61 prefix to avoid duplication
              if (phoneValue.startsWith('+61')) {
                phoneValue = phoneValue.substring(3);
              }
              
              // Remove any non-digit characters except spaces and dashes for formatting
              phoneValue = phoneValue.replace(/[^\d\s\-]/g, '');
              
              // Add +61 prefix if there's any phone number content
              if (phoneValue.trim().length > 0) {
                phoneValue = '+61' + phoneValue;
              }
              
              dispatch({type: 'set_phone', payload: phoneValue});
            }}

            sx={{ mb: 2 }}
            disabled={isSubmitting}
          />
          <TextField
            fullWidth
            select
            label="Gender"
            value={state.gender}
            onChange={e => {
              dispatch({type: 'set_gender', payload: e.target.value});
            }}
            sx={{ mb: 2 }}
            disabled={isSubmitting}
          >
            <MenuItem value="male">Male</MenuItem>
            <MenuItem value="female">Female</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </TextField>

          <DatePicker
            label="Date of Birth"
            value={state.dob}
            onChange={val => {
              dispatch({type: 'set_dob', payload: val});
            }}
            format={'DD-MM-YYYY'}
            maxDate={dayjs()}
            slotProps={{ 
              textField: { 
                fullWidth: true,
                disabled: isSubmitting
              } 
            }}
          />

          <Box display="flex" justifyContent="flex-end" mt={3}>
            <Button 
              className=" PrimaryCTAExp" 
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
            >
              {isSubmitting ? 'Creating...' : 'Confirm'}
            </Button>
          </Box>
        </Box>
      </Modal>
      
      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </LocalizationProvider>
  );
}